/**
 * Live check — the expert reviews what the live check found about their AI agent's facts.
 *
 * Before an AI consultation the agent re-checks the facts it relies on against current official
 * sources. When a fact has CHANGED it is queued here; nothing changes in the agent until the expert
 * decides (the expert is responsible for the agent's data; iMaven only proposes).
 *   Accept   → the watched fact is updated and a "Fact update" teaching is written into the agent's
 *              brain; the server returns the brain items that still state the old figures.
 *   Dismiss  → nothing changes.
 * Watched facts: last time each was confirmed on an official source; older than the server's
 * threshold (default 180 days) = stale (a weekly reminder is sent too). The expert can add, edit
 * and delete facts; edited facts are kept when iMaven updates the category defaults.
 *
 * API (Maven-platform-Server routes/liveCheckOwnerRoutes.ts): GET /live-check/me ·
 *   PUT /live-check/proposals/:id {action, note?, editedCurrent?, factText?} ·
 *   POST /live-check/facts · PUT|DELETE /live-check/facts/:id
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import { useToast } from '../Context/ToastContext';

// ============================================
// STYLES (dashboard dark palette, as AgentPage)
// ============================================
const Page = styled.div` height:100%; overflow-y:auto; background:#0f1419; color:#e6edf3; padding:28px 32px; @media (max-width:700px){ padding:20px 16px; } `;
const Title = styled.h1` font-size:24px; margin:0; font-weight:600; `;
const Subtitle = styled.p` color:#8b98a5; margin:4px 0 24px; font-size:14px; max-width:820px; line-height:1.5; `;
const Card = styled.div` background:#161b22; border:1px solid rgba(255,255,255,0.08); border-radius:12px; padding:18px 20px; margin-bottom:16px; `;
const CardTitle = styled.h3` margin:0 0 12px; font-size:15px; font-weight:600; color:#e6edf3; display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; `;
const Label = styled.label` display:block; font-size:12px; font-weight:600; color:#8b98a5; margin:12px 0 6px; text-transform:uppercase; letter-spacing:.06em; `;
const Input = styled.input` width:100%; background:#0f1419; border:1px solid rgba(255,255,255,0.12); color:#e6edf3; border-radius:8px; padding:9px 12px; font-size:14px; outline:none; &:focus{border-color:#2f81f7;} `;
const TextArea = styled.textarea` width:100%; min-height:70px; background:#0f1419; border:1px solid rgba(255,255,255,0.12); color:#e6edf3; border-radius:8px; padding:10px 12px; font-size:14px; line-height:1.5; outline:none; resize:vertical; &:focus{border-color:#2f81f7;} `;
const PrimaryBtn = styled.button` background:#2f81f7; color:#fff; border:none; border-radius:8px; padding:9px 16px; font-size:13px; font-weight:600; cursor:pointer; &:hover{background:#4f9bff;} &:disabled{opacity:.5;cursor:default;} `;
const GhostBtn = styled.button` background:transparent; color:#e6edf3; border:1px solid rgba(255,255,255,0.14); border-radius:8px; padding:9px 16px; font-size:13px; font-weight:600; cursor:pointer; &:hover{background:rgba(255,255,255,0.06);} &:disabled{opacity:.5;cursor:default;} `;
const DangerBtn = styled(GhostBtn)` color:#f85149; border-color:rgba(248,81,73,0.4); `;
const SmallBtn = styled(GhostBtn)` padding:5px 10px; font-size:12px; `;
const Row = styled.div` display:flex; gap:10px; flex-wrap:wrap; align-items:center; `;
const Help = styled.div` color:#8b98a5; font-size:12px; margin-top:6px; line-height:1.5; `;
const Pill = styled.span` display:inline-block; padding:3px 10px; border-radius:999px; font-size:12px; font-weight:600; white-space:nowrap; background:${p=>p.$bg||'rgba(255,255,255,0.08)'}; color:${p=>p.$fg||'#e6edf3'}; `;
const Stats = styled.div` display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:12px; margin-bottom:16px; `;
const Stat = styled.div` background:#161b22; border:1px solid rgba(255,255,255,0.08); border-radius:10px; padding:12px 14px; `;
const StatV = styled.div` font-size:22px; font-weight:700; color:${p=>p.$c||'#e6edf3'}; `;
const StatL = styled.div` font-size:12px; color:#8b98a5; `;
const Proposal = styled.div` border:1px solid rgba(255,255,255,0.08); border-radius:10px; padding:14px; margin-top:10px; background:#0f1419; `;
const Compare = styled.div` display:grid; grid-template-columns:1fr 1fr; gap:12px; margin:8px 0 10px; @media (max-width:800px){ grid-template-columns:1fr; } `;
const Side = styled.div` border-radius:8px; padding:10px 12px; font-size:13px; line-height:1.5; background:${p=>p.$new?'rgba(63,185,80,0.08)':'rgba(248,81,73,0.06)'}; border:1px solid ${p=>p.$new?'rgba(63,185,80,0.3)':'rgba(248,81,73,0.25)'}; b{display:block; font-size:11px; text-transform:uppercase; letter-spacing:.06em; color:#8b98a5; margin-bottom:4px;} `;
const Meta = styled.div` font-size:12px; color:#8b98a5; display:flex; gap:14px; flex-wrap:wrap; a{color:#79c0ff; word-break:break-all;} `;
const TableWrap = styled.div` overflow-x:auto; `;
const Table = styled.table` width:100%; border-collapse:collapse; font-size:13px; th{ text-align:left; color:#8b98a5; font-weight:600; font-size:12px; padding:8px; border-bottom:1px solid rgba(255,255,255,0.08); white-space:nowrap; } td{ padding:8px; border-bottom:1px solid rgba(255,255,255,0.05); vertical-align:top; } a{color:#79c0ff; word-break:break-all;} `;
const Mention = styled.div` display:flex; justify-content:space-between; gap:12px; padding:10px 0; border-top:1px solid rgba(255,255,255,0.06); font-size:13px; `;
const Snippet = styled.div` color:#c9d1d9; margin-top:4px; font-size:12px; line-height:1.5; mark{ background:rgba(210,153,34,0.35); color:#fff; border-radius:3px; padding:0 2px; } `;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');
const errText = (e, fallback) => e?.response?.data?.error || fallback;
const isUrl = (s) => /^https?:\/\//i.test(String(s || ''));
const KIND_TEXT = { opportunityMap: 'Opportunity map', strategy: 'Strategy', teaching: 'Teaching' };
const EMPTY_FACT = { fact: '', asOf: '', source: '' };

/** Highlight the old figure inside a snippet ("1500" also matches "1,500"). */
function highlight(snippet, term) {
  if (!term) return snippet;
  const digits = String(term).split('');
  const re = new RegExp(`(${digits.map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('[,\\s]?')})`);
  const parts = String(snippet).split(re);
  return parts.map((p, i) => (i % 2 === 1 ? <mark key={i}>{p}</mark> : <React.Fragment key={i}>{p}</React.Fragment>));
}

function FactStatus({ f }) {
  return (
    <Row style={{ gap: 6 }}>
      {f.stale
        ? <Pill $bg="rgba(210,153,34,0.2)" $fg="#e3b341">{f.knownTrueAt ? `Stale · ${f.ageDays} days` : 'Never verified'}</Pill>
        : <Pill $bg="rgba(63,185,80,0.2)" $fg="#56d364">Fresh</Pill>}
      {f.updatedByProposalId && <Pill $bg="rgba(47,129,247,0.2)" $fg="#79c0ff">Updated from live check</Pill>}
      {f.editedByOwner && !f.updatedByProposalId && <Pill>Edited by you</Pill>}
    </Row>
  );
}

function ProposalCard({ p, busy, onAccept, onDismiss }) {
  const [mode, setMode] = useState(null);   // null | 'accept' | 'dismiss'
  const [value, setValue] = useState(p.current || '');
  const [factText, setFactText] = useState('');
  const [note, setNote] = useState('');
  return (
    <Proposal>
      <Compare>
        <Side><b>Your agent says</b>{p.fact}</Side>
        <Side $new><b>Official source now says</b>{p.current}</Side>
      </Compare>
      <Meta>
        {p.sourceUrl && <span>Source: <a href={p.sourceUrl} target="_blank" rel="noopener noreferrer">{p.sourceUrl}</a></span>}
        {p.asOf && <span>Source date: {p.asOf}</span>}
        <span>Found {fmtDate(p.foundAt)}</span>
      </Meta>
      {mode === 'accept' && (
        <div>
          <Label>New value (edit it if the source needs a correction)</Label>
          <TextArea value={value} onChange={(e) => setValue(e.target.value)} />
          <Label>Watched fact text (optional)</Label>
          <Input value={factText} onChange={(e) => setFactText(e.target.value)} placeholder="Leave empty to use the new value" />
          <Help>The watched fact is re-checked on every live check. A full sentence (what, where, amount) checks best.</Help>
          <Label>Note (optional)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why you accept it, or what you corrected" />
          <Row style={{ marginTop: 12 }}>
            <PrimaryBtn disabled={busy || !value.trim()} onClick={() => onAccept(p, { editedCurrent: value.trim() !== p.current ? value.trim() : undefined, factText: factText.trim() || undefined, note: note.trim() || undefined })}>
              {busy ? 'Saving…' : 'Accept and update my agent'}
            </PrimaryBtn>
            <GhostBtn disabled={busy} onClick={() => setMode(null)}>Cancel</GhostBtn>
          </Row>
        </div>
      )}
      {mode === 'dismiss' && (
        <div>
          <Label>Why dismiss it? (optional)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. the source is about a different program" />
          <Row style={{ marginTop: 12 }}>
            <DangerBtn disabled={busy} onClick={() => onDismiss(p, note.trim() || undefined)}>{busy ? 'Saving…' : 'Dismiss'}</DangerBtn>
            <GhostBtn disabled={busy} onClick={() => setMode(null)}>Cancel</GhostBtn>
          </Row>
        </div>
      )}
      {!mode && (
        <Row style={{ marginTop: 12 }}>
          <PrimaryBtn disabled={busy} onClick={() => setMode('accept')}>Accept…</PrimaryBtn>
          <GhostBtn disabled={busy} onClick={() => setMode('dismiss')}>Dismiss…</GhostBtn>
        </Row>
      )}
    </Proposal>
  );
}

export default function LiveCheckPage() {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [lastAccept, setLastAccept] = useState(null);    // { proposal, mentions, searchedFor, teachingError }
  const [onlyStale, setOnlyStale] = useState(false);
  const [editing, setEditing] = useState(null);          // fact id | 'new'
  const [factForm, setFactForm] = useState(EMPTY_FACT);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/live-check/me');
      setData(r.data);
    } catch (e) {
      showToast(errText(e, 'Could not load the live check'), 'error');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const facts = useMemo(() => data?.facts || [], [data]);
  const staleCount = facts.filter((f) => f.stale).length;
  const pending = data?.proposals?.proposed || [];
  const history = [...(data?.proposals?.accepted || []), ...(data?.proposals?.dismissed || [])]
    .sort((a, b) => new Date(b.reviewedAt || b.foundAt) - new Date(a.reviewedAt || a.foundAt));
  const shownFacts = onlyStale ? facts.filter((f) => f.stale) : facts;

  const accept = async (p, body) => {
    setBusyId(p._id);
    try {
      const r = await api.put(`/live-check/proposals/${p._id}`, { action: 'accept', ...body });
      setLastAccept(r.data);
      showToast(r.data?.teachingError ? 'Fact updated, but the brain teaching could not be written. Try again or add it in Synthora.' : 'Accepted. Your agent now uses the new value.', r.data?.teachingError ? 'warning' : 'success');
      await load();
    } catch (e) { showToast(errText(e, 'Could not accept the update'), 'error'); }
    finally { setBusyId(null); }
  };

  const dismiss = async (p, note) => {
    setBusyId(p._id);
    try {
      await api.put(`/live-check/proposals/${p._id}`, { action: 'dismiss', note });
      showToast('Dismissed. Nothing changed in your agent.', 'success');
      await load();
    } catch (e) { showToast(errText(e, 'Could not dismiss the update'), 'error'); }
    finally { setBusyId(null); }
  };

  const startEdit = (f) => { setEditing(f ? f._id : 'new'); setFactForm(f ? { fact: f.fact || '', asOf: f.asOf || '', source: f.source || '' } : EMPTY_FACT); };
  const saveFact = async () => {
    if (!factForm.fact.trim()) { showToast('Write the fact.', 'error'); return; }
    setBusyId('fact');
    try {
      if (editing === 'new') await api.post('/live-check/facts', factForm);
      else await api.put(`/live-check/facts/${editing}`, factForm);
      showToast('Watched fact saved.', 'success');
      setEditing(null);
      await load();
    } catch (e) { showToast(errText(e, 'Could not save the fact'), 'error'); }
    finally { setBusyId(null); }
  };
  const deleteFact = async (f) => {
    if (!window.confirm(`Stop watching this fact?\n\n${f.fact}`)) return;
    setBusyId(f._id);
    try {
      await api.delete(`/live-check/facts/${f._id}`);
      showToast('Fact removed.', 'success');
      await load();
    } catch (e) { showToast(errText(e, 'Could not remove the fact'), 'error'); }
    finally { setBusyId(null); }
  };

  const openMention = (m) => {
    navigate(m.link);
  };

  if (loading) return <Page><Title>Live check</Title><Subtitle>Loading…</Subtitle></Page>;

  return (
    <Page>
      <Row style={{ justifyContent: 'space-between' }}>
        <Title>Live check</Title>
        <GhostBtn onClick={() => navigate('/agent')}>My AI Agents</GhostBtn>
      </Row>
      <Subtitle>
        Before writing a consultation, your AI agent re-checks the facts it relies on against current official sources.
        When a fact has changed, it appears here. Nothing changes in your agent until you decide: you are responsible for
        the information your agent gives, and the live check only proposes.
      </Subtitle>

      {!data?.available ? (
        <Card>
          <CardTitle>Not available yet</CardTitle>
          <Help>Live check is not available for your agent's category yet. When it is, the facts your agent relies on will appear here.</Help>
        </Card>
      ) : (
        <>
          {data.borrowedFrom && (
            <Card style={{ borderColor: 'rgba(47,129,247,0.4)' }}>
              <Help style={{ marginTop: 0 }}>
                Your agent uses its category's default watched facts. The first change you make, or the first change a live check finds, gives your agent its own copy.
              </Help>
            </Card>
          )}

          <Stats>
            <Stat><StatV $c={pending.length ? '#e3b341' : undefined}>{pending.length}</StatV><StatL>Changes to review</StatL></Stat>
            <Stat><StatV>{facts.length}</StatV><StatL>Watched facts</StatL></Stat>
            <Stat><StatV $c={staleCount ? '#e3b341' : '#56d364'}>{staleCount}</StatV><StatL>Not verified for {data.staleAfterDays}+ days</StatL></Stat>
            <Stat><StatV>{data.stats?.runs ?? 0}</StatV><StatL>Checks run{data.stats?.lastRunAt ? ` · last ${fmtDate(data.stats.lastRunAt)}` : ''}</StatL></Stat>
          </Stats>

          {lastAccept && (
            <Card style={{ borderColor: lastAccept.mentions?.length ? 'rgba(210,153,34,0.5)' : 'rgba(63,185,80,0.4)' }}>
              <CardTitle>
                <span>{lastAccept.mentions?.length ? 'Still mentioned in your brain' : 'Your agent is updated'}</span>
                <SmallBtn onClick={() => setLastAccept(null)}>Close</SmallBtn>
              </CardTitle>
              <Help style={{ marginTop: 0 }}>
                {lastAccept.teachingError
                  ? 'The watched fact was updated, but the teaching could not be written into your brain.'
                  : 'The watched fact is updated and a "Fact update" teaching now tells your agent the current value.'}
                {' '}
                {lastAccept.searchedFor?.length
                  ? (lastAccept.mentions?.length
                    ? `These items still state the old figure (${lastAccept.searchedFor.join(', ')}). Open each one and correct it.`
                    : `No other item in your brain states the old figure (${lastAccept.searchedFor.join(', ')}).`)
                  : 'The old statement has no figure to search for; check your opportunity maps and strategies for wording that relies on it.'}
              </Help>
              {(lastAccept.mentions || []).map((m, i) => (
                <Mention key={`${m.itemId}-${m.field}-${i}`}>
                  <div style={{ minWidth: 0 }}>
                    <div><Pill>{KIND_TEXT[m.kind] || m.kind}</Pill> <b>{m.itemName}</b>{m.pathwayName ? ` → ${m.pathwayName}` : ''} <span style={{ color: '#8b98a5' }}>· {m.field}</span></div>
                    <Snippet>{highlight(m.snippet, m.term)}</Snippet>
                  </div>
                  <SmallBtn onClick={() => openMention(m)}>Open</SmallBtn>
                </Mention>
              ))}
            </Card>
          )}

          <Card>
            <CardTitle><span>Changes found by the live check</span>{pending.length > 0 && <Pill $bg="rgba(210,153,34,0.2)" $fg="#e3b341">{pending.length} to review</Pill>}</CardTitle>
            {pending.length === 0
              ? <Help style={{ marginTop: 0 }}>Nothing to review. When a live check finds that a fact has changed, it appears here and you get a notification.</Help>
              : pending.map((p) => <ProposalCard key={p._id} p={p} busy={busyId === p._id} onAccept={accept} onDismiss={dismiss} />)}
          </Card>

          <Card>
            <CardTitle>
              <span>Watched facts</span>
              <Row>
                <label style={{ fontSize: 13, color: '#8b98a5', cursor: 'pointer' }}>
                  <input type="checkbox" checked={onlyStale} onChange={(e) => setOnlyStale(e.target.checked)} style={{ marginRight: 6 }} />
                  Only stale
                </label>
                <SmallBtn onClick={() => startEdit(null)} disabled={editing === 'new'}>Add fact</SmallBtn>
              </Row>
            </CardTitle>
            <Help style={{ marginTop: 0, marginBottom: 10 }}>
              The figures, thresholds and deadlines your agent relies on. Every live check re-verifies the ones a case needs; a fact not confirmed
              for {data.staleAfterDays} days is marked stale and you get a weekly reminder. Facts you edit are kept when iMaven updates the category defaults.
            </Help>
            {editing === 'new' && (
              <Proposal>
                <Label>Fact</Label>
                <TextArea value={factForm.fact} onChange={(e) => setFactForm({ ...factForm, fact: e.target.value })} placeholder="e.g. The federal minimum wage in Canada is $17.75 an hour" />
                <Row>
                  <div style={{ flex: '1 1 160px' }}><Label>As of</Label><Input value={factForm.asOf} onChange={(e) => setFactForm({ ...factForm, asOf: e.target.value })} placeholder="2026-04-01" /></div>
                  <div style={{ flex: '3 1 300px' }}><Label>Official source</Label><Input value={factForm.source} onChange={(e) => setFactForm({ ...factForm, source: e.target.value })} placeholder="https://…" /></div>
                </Row>
                <Row style={{ marginTop: 12 }}>
                  <PrimaryBtn disabled={busyId === 'fact'} onClick={saveFact}>Save</PrimaryBtn>
                  <GhostBtn onClick={() => setEditing(null)}>Cancel</GhostBtn>
                </Row>
              </Proposal>
            )}
            {shownFacts.length === 0 ? (
              <Help>{onlyStale ? 'No stale facts.' : 'No watched facts yet.'}</Help>
            ) : (
              <TableWrap>
                <Table>
                  <thead><tr><th>Fact</th><th>As of</th><th>Last verified</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {shownFacts.map((f) => (editing === f._id ? (
                      <tr key={f._id}>
                        <td colSpan={5}>
                          <Label>Fact</Label>
                          <TextArea value={factForm.fact} onChange={(e) => setFactForm({ ...factForm, fact: e.target.value })} />
                          <Row>
                            <div style={{ flex: '1 1 160px' }}><Label>As of</Label><Input value={factForm.asOf} onChange={(e) => setFactForm({ ...factForm, asOf: e.target.value })} /></div>
                            <div style={{ flex: '3 1 300px' }}><Label>Official source</Label><Input value={factForm.source} onChange={(e) => setFactForm({ ...factForm, source: e.target.value })} /></div>
                          </Row>
                          <Row style={{ marginTop: 12 }}>
                            <PrimaryBtn disabled={busyId === 'fact'} onClick={saveFact}>Save</PrimaryBtn>
                            <GhostBtn onClick={() => setEditing(null)}>Cancel</GhostBtn>
                          </Row>
                        </td>
                      </tr>
                    ) : (
                      <tr key={f._id}>
                        <td style={{ minWidth: 260 }}>
                          {f.fact}
                          {f.source && <div style={{ marginTop: 4, fontSize: 12 }}>{isUrl(f.source) ? <a href={f.source} target="_blank" rel="noopener noreferrer">{f.source}</a> : <span style={{ color: '#8b98a5' }}>{f.source}</span>}</div>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{f.asOf || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {f.lastVerifiedAt ? fmtDate(f.lastVerifiedAt) : '—'}
                          {f.lastVerifiedSource && <div style={{ fontSize: 12 }}><a href={f.lastVerifiedSource} target="_blank" rel="noopener noreferrer">source</a></div>}
                        </td>
                        <td><FactStatus f={f} /></td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <Row style={{ gap: 6, flexWrap: 'nowrap' }}>
                            <SmallBtn onClick={() => startEdit(f)}>Edit</SmallBtn>
                            <SmallBtn disabled={busyId === f._id} onClick={() => deleteFact(f)} style={{ color: '#f85149' }}>Delete</SmallBtn>
                          </Row>
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </Card>

          {history.length > 0 && (
            <Card>
              <CardTitle>
                <span>Reviewed changes ({history.length})</span>
                <SmallBtn onClick={() => setShowHistory((v) => !v)}>{showHistory ? 'Hide' : 'Show'}</SmallBtn>
              </CardTitle>
              {showHistory && history.map((p) => (
                <Proposal key={p._id}>
                  <Row style={{ marginBottom: 6 }}>
                    {p.status === 'accepted'
                      ? <Pill $bg="rgba(63,185,80,0.2)" $fg="#56d364">Accepted</Pill>
                      : <Pill $bg="rgba(139,152,165,0.2)" $fg="#c9d1d9">Dismissed</Pill>}
                    <span style={{ fontSize: 12, color: '#8b98a5' }}>{fmtDate(p.reviewedAt)}{p.reviewedBy ? ` by ${p.reviewedBy}` : ''}</span>
                  </Row>
                  <div style={{ fontSize: 13 }}><span style={{ color: '#8b98a5' }}>Was:</span> {p.fact}</div>
                  <div style={{ fontSize: 13 }}><span style={{ color: '#8b98a5' }}>{p.status === 'accepted' ? 'Now:' : 'Proposed:'}</span> {p.current}{p.proposedCurrent ? <span style={{ color: '#8b98a5' }}> (edited from "{p.proposedCurrent}")</span> : null}</div>
                  {p.note && <Help>Note: {p.note}</Help>}
                </Proposal>
              ))}
            </Card>
          )}
        </>
      )}
    </Page>
  );
}
